import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface KcCitation {
  /** Kept for back-compat; the slim card no longer renders a number chip. */
  num?: number;
  title: string;
  url?: string;
}

/**
 * A grounded "Ask AI" answer — calm single-card treatment: optional question heading, the answer,
 * subtle clickable sources, and one quiet provenance + not-financial-advice footer. Set
 * `dismissible` to show a close affordance (emits `dismiss`).
 *
 * Feature-local copy of the former `@pitchin/ui` `pin-kc-ai-answer` (ADR-0006).
 */
@Component({
  selector: 'kc-ai-answer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="tw-rounded-[16px] tw-border tw-border-[#ececec] tw-bg-white tw-shadow-[0_6px_28px_rgba(0,0,0,0.06)]">
      <div class="tw-px-6 tw-pt-5 tw-pb-[16px]">
        <!-- question (optional) -->
        <div *ngIf="question || dismissible" class="tw-flex tw-items-start tw-gap-3 tw-mb-[10px]">
          <span class="tw-flex-1 tw-text-[16px] tw-text-[#1a1a1a] tw-font-semibold tw-leading-snug">{{ question }}</span>
          <button
            *ngIf="dismissible"
            type="button"
            (click)="dismiss.emit()"
            aria-label="Close"
            class="tw-text-[20px] tw-leading-none tw-text-[#c4c4c4] tw-bg-transparent tw-border-none tw-cursor-pointer tw-flex-shrink-0 hover:tw-text-[#6a6a6a]"
          >&times;</button>
        </div>

        <!-- loading skeleton -->
        <div *ngIf="loading" class="tw-animate-pulse tw-space-y-2 tw-py-1">
          <div class="tw-h-3 tw-bg-[#f0f0f0] tw-rounded tw-w-full"></div>
          <div class="tw-h-3 tw-bg-[#f0f0f0] tw-rounded tw-w-4/5"></div>
          <div class="tw-h-3 tw-bg-[#f0f0f0] tw-rounded tw-w-3/5"></div>
        </div>

        <!-- answer -->
        <p *ngIf="!loading" class="tw-text-[15px] tw-leading-[1.65] tw-text-[#3a3a3a] tw-m-0">{{ answer }}</p>

        <!-- sources — subtly clickable -->
        <div *ngIf="!loading && citations.length" class="tw-flex tw-flex-col tw-gap-[7px] tw-pt-[16px]">
          <span class="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[.05em] tw-text-[#b0b0b0]">Sources</span>
          <ng-container *ngFor="let c of citations">
            <a
              *ngIf="c.url"
              [href]="c.url"
              class="tw-inline-flex tw-items-center tw-gap-[7px] tw-text-[13.5px] tw-text-[#005DD1] tw-cursor-pointer tw-no-underline tw-w-fit hover:tw-text-[#0047a3]"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tw-flex-shrink-0 tw-opacity-80"><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z"/><path d="M8 7h7M8 11h6"/></svg>
              <span class="tw-underline tw-decoration-1 tw-underline-offset-[3px] tw-decoration-[#b9d3f1]">{{ c.title }}</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="tw-flex-shrink-0 tw-opacity-50"><path d="M9 18l6-6-6-6"/></svg>
            </a>
            <span
              *ngIf="!c.url"
              aria-disabled="true"
              class="tw-inline-flex tw-items-center tw-gap-[7px] tw-text-[13.5px] tw-text-[#9aa0a6] tw-cursor-default tw-w-fit"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tw-flex-shrink-0 tw-opacity-70"><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z"/><path d="M8 7h7M8 11h6"/></svg>
              <span>{{ c.title }}</span>
            </span>
          </ng-container>
        </div>
      </div>

      <!-- footer: provenance + disclaimer -->
      <div class="tw-flex tw-items-center tw-gap-[7px] tw-px-6 tw-py-[13px] tw-border-t tw-border-[#f1f1f1] tw-text-[11.5px] tw-text-[#9aa0a6]">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="tw-text-primary tw-flex-shrink-0"><path d="M12 2l1.9 4.6L18.5 8l-3.8 3 1.1 5L12 13.8 8.2 16l1.1-5L5.5 8l4.6-1.4z"/></svg>
        <span class="tw-flex-1">pitchIN AI · grounded in our official help</span>
        <span>Not financial advice</span>
      </div>
    </div>
  `,
})
export class KcAiAnswerComponent {
  @Input() question?: string;
  @Input() answer = '';
  @Input() citations: KcCitation[] = [];
  @Input() loading = false;
  /** When true, shows a × close control next to the question. */
  @Input() dismissible = false;
  @Output() dismiss = new EventEmitter<void>();
}
